import { Fragment } from 'react';
import { getElementIdFromHash } from '@pm2/common-utils';
import { flashcardComponentJsxPrinters } from '@pm2/flashcard-component';
import { geogebraComponentJsxPrinters } from '@pm2/geogebra-component';
import { iArkComponentJsxPrinters } from '@pm2/i-ark-component';
import { iGuideComponentJsxPrinters } from '@pm2/i-guide-component';
import { iframeComponentJsxPrinters } from '@pm2/iframe-component';
import { imageComponentJsxPrinters } from '@pm2/image-component';
import { imageHotspotsComponentJsxPrinters } from '@pm2/image-hotspots-component';
import { mediaComponentJsxPrinters } from '@pm2/media-component';
import { opgavekompleksetComponentJsxPrinters } from '@pm2/opgavekomplekset-component';
import { sliderComponentJsxPrinters } from '@pm2/slider-component';
import { textComponentJsxPrinters } from '@pm2/text-component';
import { componentFrameworkErrorJsxPrinters } from '@pm2/ui-components';
import { generateJsxKey } from '@utils/ast';
import { isPlatformNodeJS } from '@utils/common';
import { AccordionComponent } from './accordion-component.js';
import { jsx } from 'react/jsx-runtime';

const printAccordionComponent = (node, context) => {
  const children = node.children.filter(it => it.children.length > 0);
  if (children.length === 0) {
    // if no content elements were contained inside the panel, then hide it all
    // together
    return null;
  }

  /**
   * detect the index of the accordion to which should be expanded due to hash
   * changes (if any)
   */
  const onHashChanged = () => {
    for (let i = 0; i < node.children.length; i++) {
      const panel = node.children[i];
      if (panel && shouldExpandPanelInitially(panel)) {
        return i;
      }
    }
    return undefined;
  };
  const initiallyExpandedPanel = children.find(panel => shouldExpandPanelInitially(panel));
  return /*#__PURE__*/jsx(AccordionComponent, {
    autoCollapse: node.options.autoCollapse,
    onHashChange: onHashChanged,
    panels: children.map(panel => ({
      expandedInitially: (() => {
        if (!initiallyExpandedPanel) {
          // if an initially expanded panel wasn't requested by URL, then use
          // the setting of the panel "as is"
          return panel.options.expandedInitially;
        }
        if (initiallyExpandedPanel === panel) {
          // if this panel was explicitly requested to be expanded initially
          // by URL, then enforce this to happen
          return true;
        }

        // ... otherwise, if another panel was explicitly requested by URL,
        // then only honor the built-in expandedInitially configuration if the
        // AccordionComponent hasn't been configured with autoCollapse as this
        // prevents us from ever opening more than one panel at a time
        return !node.options.autoCollapse ? panel.options.expandedInitially : false;
      })(),
      title: panel.meta.title,
      titleTag: panel.options.titleTag,
      children: context.printChildren(panel, {
        "accordion-component.panel": printAccordionPanel
      })
    }))
  }, generateJsxKey(context));
};
function shouldExpandPanelInitially(panel) {
  // if panel hasn't explicitly been configured to auto-expand, then scan to see
  // if it contains an element which we have been deep-linked to
  const deepLinkedLearningObjectId = (() => {
    if (isPlatformNodeJS()) {
      return undefined;
    }
    return getElementIdFromHash(window.location.hash);
  })();
  return !!deepLinkedLearningObjectId && panel.children.some(it => "pm2" in it && it.id === deepLinkedLearningObjectId);
}
const printAccordionPanel = (node, context) => {
  return /*#__PURE__*/jsx(Fragment, {
    children: context.printChildren(node.children, {
      ...componentFrameworkErrorJsxPrinters,
      ...flashcardComponentJsxPrinters,
      ...geogebraComponentJsxPrinters,
      ...iArkComponentJsxPrinters,
      ...iframeComponentJsxPrinters,
      ...imageHotspotsComponentJsxPrinters,
      ...iGuideComponentJsxPrinters,
      ...imageComponentJsxPrinters,
      ...mediaComponentJsxPrinters,
      ...opgavekompleksetComponentJsxPrinters,
      ...sliderComponentJsxPrinters,
      ...textComponentJsxPrinters
    })
  }, generateJsxKey(context));
};
const accordionComponentJsxPrinters = {
  "accordion-component": printAccordionComponent
};

export { accordionComponentJsxPrinters, printAccordionComponent };
//# sourceMappingURL=printer.to-jsx.js.map
